/**
 * RoleName Value Object
 *
 * Encapsulates validation logic for Role name field.
 *
 * @module domains/personnel/domain/value-objects/RoleName
 */

/**
 * Role Name branded type
 */
export type RoleName = string & { readonly __brand: 'RoleName' };

/** Maximum allowed length for a role name */
export const ROLE_NAME_MAX_LENGTH = 255;

/**
 * Validates if a string is a valid role name
 *
 * @param value - String to validate
 * @returns Validation result with error message if invalid
 *
 * @example
 * const result = validateRoleName('Manager');
 * if (result.valid) {
 *   console.log('Valid role name');
 * }
 */
export const validateRoleName = (value: string): { valid: boolean; error?: string } => {
  const trimmed = value?.trim() || '';

  if (!trimmed) {
    return { valid: false, error: 'Role name is required' };
  }

  if (trimmed.length > ROLE_NAME_MAX_LENGTH) {
    return { valid: false, error: `Role name exceeds ${ROLE_NAME_MAX_LENGTH} characters` };
  }

  return { valid: true };
};

/**
 * Creates a validated RoleName
 *
 * @param value - The role name string
 * @returns RoleName branded type
 * @throws {Error} If validation fails
 *
 * @example
 * const roleName = createRoleName('Manager');
 * // roleName is now of type RoleName
 */
export const createRoleName = (value: string): RoleName => {
  const result = validateRoleName(value);

  if (!result.valid) {
    throw new Error(result.error);
  }

  return value.trim() as RoleName;
};
